import React, { useState, useEffect } from 'react';
import { PlanFrequencyEnum, PrismicApiPlan } from '@landr/core.models';
import { Text, Box, useBreakpoint, Badge, Paragraph } from '@landr/maestro';
import { Button } from '@landr/maestro/experimental';
import { Currency, Price } from '@landr/pricing';
import { PrismicRichTextFieldType, PrismicInternalLink } from 'components/Slices/types';
import { PrismicLink } from 'components/PrismicLink';
import { PricingPlanEnum } from 'enum/pricingPlan';
import { getPlanFrequency, getPlanFrequencyLength } from '../common/helpers';
import { CommonPricingTranslations, ApiPlanFrequency, PricingPlanDocument } from '../types';
import { StyledPlan, StyledPlanDescription, StyledPlanPrice, StyledFeaturedPrice, StyledLink } from '../styled';
import { usePricingContext } from '../common/PricingContext';
import { PricingPlanDetails } from './PricingPlanDetails';

export type PrismicPricingPlan = {
    api_plan: PrismicApiPlan;
    partner_plans: [];
    name: string;
    pricing_description: [{ text: string }];
    pricing_cta: string;
    products: [{ product: PrismicPricingPlanProduct }];
};

export type PrismicPricingPlanProduct = {
    id: string;
};

export type PartnerPlan = {
    partner_plan: PrismicApiPlan;
};

const addCodeParamToCtaLink = (
    link: PrismicInternalLink,
    planDocument: PricingPlanDocument,
    toggleFrequency: string,
): PrismicInternalLink => {
    const plan: string = planDocument.api_plan.code;
    let ctaLink;

    // Verify that the plan exists, since distribution_freemium is a fake plan
    if (Object.keys(PricingPlanEnum).includes(plan)) {
        const singlePlanFrequency =
            planDocument.api_plan.frequencies.length &&
            planDocument.api_plan.frequencies[0].interval === PlanFrequencyEnum.Yearly
                ? PlanFrequencyEnum.Yearly
                : PlanFrequencyEnum.Monthly;

        const planFrequency = getPlanFrequencyLength(planDocument.api_plan) > 1 ? toggleFrequency : singlePlanFrequency;

        ctaLink = {
            ...link,
            queryParams: {
                plan,
                planFrequency,
            },
        };
    } else {
        ctaLink = {
            ...link,
        };
    }

    return ctaLink;
};

export type PlanNameProps = { name: string; description: PrismicRichTextFieldType };

const PlanName: React.FC<PlanNameProps> = ({ name, description }) => {
    if (!name || !description) {
        return null;
    }

    return (
        <>
            <Text size="xl" fontWeight="bold">
                {name}
            </Text>

            <StyledPlanDescription content={description} />
        </>
    );
};

export type PlanPriceProps = {
    frequency: ApiPlanFrequency;
    currencyCode: string;
    translations: CommonPricingTranslations;
    isBasicPlan: boolean;
    isFeatured: boolean;
    languageCode: string;
};

const PlanPrice: React.FC<PlanPriceProps> = ({ frequency, currencyCode, translations, isBasicPlan, languageCode }) => {
    if (!frequency) {
        return null;
    }

    const prismicPrice = frequency.prices && frequency.prices.find((p) => p.currencyCode === currencyCode);
    if (!prismicPrice) {
        return null;
    }
    const isMobile = useBreakpoint('md');

    const price = new Price(prismicPrice);

    const yearlySaving =
        languageCode === 'ja'
            ? `${prismicPrice.yearlyDiscountPercent * 100}% ${translations.saving_yearly}`
            : `${translations.saving_yearly} ${prismicPrice.yearlyDiscountPercent * 100}%`;

    return (
        <>
            {prismicPrice.amount && prismicPrice.yearlyDiscount ? (
                <Box display="flex" justifyContent="center" height={isMobile ? 'auto' : '40px'}>
                    {!isBasicPlan && !isMobile && (
                        <Paragraph textAlign={'center'} color={'primary.500'}>
                            <StyledFeaturedPrice as={'span'}>
                                {languageCode === 'ja' ? (
                                    <>
                                        <Text>{translations.per_year}</Text>{' '}
                                        <Currency
                                            amount={prismicPrice.yearlyDiscount + prismicPrice.amount}
                                            price={price}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <Currency
                                            amount={prismicPrice.yearlyDiscount + prismicPrice.amount}
                                            price={price}
                                        />
                                        {' / '} <Text>{translations.per_year}</Text>
                                    </>
                                )}
                            </StyledFeaturedPrice>
                            {' - '}
                            <b>{yearlySaving}</b>
                        </Paragraph>
                    )}
                </Box>
            ) : (
                <></>
            )}
            <Box marginY="xl" fontWeight="bold">
                {prismicPrice.amount ? (
                    <>
                        <StyledPlanPrice>
                            <Currency amount={prismicPrice.amount} price={price} />
                        </StyledPlanPrice>
                        {' / '}
                        {frequency.interval === PlanFrequencyEnum.Monthly
                            ? translations.per_month
                            : translations.per_year}
                    </>
                ) : (
                    <>
                        <StyledPlanPrice>{translations.free}</StyledPlanPrice>
                    </>
                )}
            </Box>
        </>
    );
};

export type PricingPlanProps = {
    plan: PricingPlanDocument;
    frequency: PlanFrequencyEnum;
    setYearlySaving: React.Dispatch<React.SetStateAction<any>>;
    languageCode: string;
};

export const PricingPlan: React.FC<PricingPlanProps> = ({ plan, frequency, setYearlySaving, languageCode }) => {
    const isLarge = useBreakpoint('lg');
    const isMobile = useBreakpoint('md');
    const { translations, countryCurrency, sliceContext } = usePricingContext();
    const apiPlanFrequency = getPlanFrequency(plan.api_plan, frequency);
    const [currentFrequency, setCurrentFrequency] = useState(apiPlanFrequency);

    useEffect(() => {
        setCurrentFrequency(getPlanFrequency(plan.api_plan, frequency));
        if (countryCurrency) {
            const yearlyPricesPlans = getPlanFrequency(plan.api_plan, PlanFrequencyEnum.Yearly);
            const prismicYearlyPrice =
                yearlyPricesPlans.prices &&
                yearlyPricesPlans.prices.find((p) => p.currencyCode === countryCurrency.currency);
            setYearlySaving(prismicYearlyPrice && prismicYearlyPrice.yearlyDiscountPercent * 100);
        }
    }, [frequency, countryCurrency, apiPlanFrequency]);

    const isFeatured = plan.api_plan && plan.api_plan.code === 'mastering_advanced' && !isMobile;

    // Basic tab is the only one without the saving copy, pro plan doesn't have the featured UX but have the copy.
    const isBasicPlan = plan.api_plan && plan.api_plan.code === 'mastering_basic';

    let featuredButtonColor = 'primary';
    if (isFeatured) {
        featuredButtonColor = 'accent';
    }

    return countryCurrency ? (
        <StyledPlan isLarge={isLarge} isFeatured={isFeatured}>
            {isFeatured && (
                <Badge isRounded variant={'secondary'} size={'sm'} mb={16} content={translations.best_recommended} />
            )}
            <PlanName name={plan.name} description={plan.pricing_description}></PlanName>
            <PlanPrice
                isBasicPlan={isBasicPlan}
                isFeatured={isFeatured}
                frequency={currentFrequency}
                currencyCode={countryCurrency.currency}
                translations={translations}
                languageCode={languageCode}
            ></PlanPrice>
            <StyledLink>
                <PrismicLink
                    link={addCodeParamToCtaLink(plan.pricing_cta_link, plan, apiPlanFrequency.interval)}
                    slice={sliceContext}
                >
                    <Button variant={featuredButtonColor} size={'sm'}>
                        {plan.pricing_cta}
                    </Button>
                </PrismicLink>
            </StyledLink>

            <PricingPlanDetails
                plan={plan}
                frequency={frequency}
                countryCurrency={countryCurrency}
            ></PricingPlanDetails>
        </StyledPlan>
    ) : null;
};
